import { useEffect, type RefObject } from 'react';

import { clearFocusReturn, useFocusReturnRequest } from './focusReturnStore.ts';
import type { ObjectSidebarPanel } from './useObjectSidebar.tsx';

/**
 * Requests older than this are discarded rather than honoured, so a request that
 * never found a mounted trigger (e.g. the panel was closed on a surface without
 * one) cannot steal focus on a later page.
 */
export const FOCUS_RETURN_MAX_AGE_MS = 2000;

/**
 * `element.focus()` is silently ignored while the element or any ancestor is
 * `display: none`. The object sidebar control bar is hidden that way while a
 * panel is open and is un-hidden in a *later* commit than the one that runs the
 * trigger's effect, so we must wait for it rather than focus blindly.
 */
const isHiddenByDisplayNone = (element: HTMLElement): boolean => {
	for (let node: HTMLElement | null = element; node; node = node.parentElement) {
		if (getComputedStyle(node).display === 'none') {
			return true;
		}
	}
	return false;
};

/**
 * Moves keyboard focus to `ref` when the panel identified by `panelId` has just
 * been closed by the user — the "return focus to the invoker" step of the
 * WAI-ARIA dialog pattern.
 *
 * Use this in the component that renders a panel's trigger button. Because the
 * effect runs after commit and retries per animation frame until focus actually
 * lands (or the request expires), it works whether the trigger stayed mounted
 * while the panel was open, is still hidden with `display: none` when the panel
 * closes, or was unmounted and remounted (including lazy-loaded triggers).
 *
 * If `ref.current` is not set (e.g. a collapsed variant that renders no button),
 * the request is left pending for another instance to consume.
 */
export const useReturnFocusOnPanelClose = (
	panelId: ObjectSidebarPanel['id'],
	ref: RefObject<HTMLElement | null>,
): void => {
	const request = useFocusReturnRequest();

	// Intentionally no dependency array: `ref.current` is not reactive, so a
	// trigger that starts rendering its button in a later commit (without the
	// request changing) must still get a chance to consume the pending request.
	// The body is a cheap early-return when nothing is pending.
	useEffect(() => {
		if (!request || request.panelId !== panelId) {
			return;
		}

		let frame: number | undefined;
		let cancelled = false;

		const attempt = (): void => {
			if (cancelled) {
				return;
			}

			if (Date.now() - request.requestedAt > FOCUS_RETURN_MAX_AGE_MS) {
				clearFocusReturn();
				return;
			}

			const element = ref.current;
			if (!element) {
				return;
			}

			if (!isHiddenByDisplayNone(element)) {
				element.focus();
				if (document.activeElement === element) {
					clearFocusReturn();
					return;
				}
			}

			// Not focusable yet (still hidden, or focus was refused) — try again next frame.
			frame = requestAnimationFrame(attempt);
		};

		attempt();

		return () => {
			cancelled = true;
			if (frame !== undefined) {
				cancelAnimationFrame(frame);
			}
		};
	});
};
